import React, { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, CreditCard, Store, AlertCircle, Loader2, DollarSign, Filter, Package, FileText, X, ChevronRight } from 'lucide-react';
import { useCardTransactionPatterns, useWalletTransactionDetails, useReasonCodeDistribution, useMerchantsByReasonCode } from '../hooks/useReports';
import DataTable from '../components/DataTable';
import QueryInfoButton from '../components/QueryInfoButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CARD_METRICS_QUERIES } from '../constants/queries';

const COLORS = {
  decline: '#EF4444',
  accept: '#22C55E',
};

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toLocaleString() || '0';
}

function StatCard({ title, value, subtitle, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

export default function CardTransactionPatterns() {
  const { data, isLoading, isError, error } = useCardTransactionPatterns();
  const { data: productData, isLoading: productLoading } = useWalletTransactionDetails();
  const { data: reasonCodeData, isLoading: reasonCodeLoading } = useReasonCodeDistribution();
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedReasonCode, setSelectedReasonCode] = useState(null);

  // Fetch merchants for selected reason code
  const { data: merchantsData, isLoading: merchantsLoading } = useMerchantsByReasonCode(selectedReasonCode);

  // Extract unique tenants from data
  const tenants = useMemo(() => {
    if (!data?.data || data.data.length === 0) return [];

    const tenantMap = new Map();
    data.data.forEach((txn) => {
      if (txn.tenant_code && !tenantMap.has(txn.tenant_code)) {
        tenantMap.set(txn.tenant_code, {
          tenant_code: txn.tenant_code,
          tenant_name: txn.tenant_name || txn.tenant_code,
        });
      }
    });

    return Array.from(tenantMap.values()).sort((a, b) =>
      (a.tenant_name || '').localeCompare(b.tenant_name || '')
    );
  }, [data]);

  // Filter data by selected tenant
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (selectedTenant === 'all') return data.data;
    return data.data.filter((txn) => txn.tenant_code === selectedTenant);
  }, [data, selectedTenant]);

  // Process transaction data to find patterns
  const patterns = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return {
        topDeclineMerchants: [],
        topAcceptMerchants: [],
        totalTransactions: 0,
        totalDeclines: 0,
        totalAccepts: 0,
        totalDeclineAmount: 0,
        totalAcceptAmount: 0,
      };
    }

    const merchantStats = {};
    let totalDeclines = 0;
    let totalAccepts = 0;
    let totalDeclineAmount = 0;
    let totalAcceptAmount = 0;

    filteredData.forEach((txn) => {
      const merchant = txn.merchant_name || 'Unknown';
      const amount = parseFloat(txn.txn_total_dollars) || 0;
      const isDecline = (txn.transaction_type_detail || '').toLowerCase().includes('decline') ||
                        (txn.transaction_description || '').toLowerCase().includes('decline') ||
                        (txn.transaction_type || '').toLowerCase().includes('decline');

      if (!merchantStats[merchant]) {
        merchantStats[merchant] = {
          merchant_name: merchant,
          decline_count: 0,
          accept_count: 0,
          decline_amount: 0,
          accept_amount: 0,
          total_count: 0,
        };
      }

      merchantStats[merchant].total_count++;

      if (isDecline) {
        merchantStats[merchant].decline_count++;
        merchantStats[merchant].decline_amount += amount;
        totalDeclines++;
        totalDeclineAmount += amount;
      } else {
        merchantStats[merchant].accept_count++;
        merchantStats[merchant].accept_amount += amount;
        totalAccepts++;
        totalAcceptAmount += amount;
      }
    });

    const merchantList = Object.values(merchantStats);

    // Top 10 decline merchants by count
    const topDeclineMerchants = [...merchantList]
      .filter((m) => m.decline_count > 0)
      .sort((a, b) => b.decline_count - a.decline_count)
      .slice(0, 10)
      .map((m, idx) => ({
        ...m,
        rank: idx + 1,
        decline_rate: ((m.decline_count / m.total_count) * 100).toFixed(1),
      }));

    // Top 10 accept merchants by count
    const topAcceptMerchants = [...merchantList]
      .filter((m) => m.accept_count > 0)
      .sort((a, b) => b.accept_count - a.accept_count)
      .slice(0, 10)
      .map((m, idx) => ({
        ...m,
        rank: idx + 1,
        accept_rate: ((m.accept_count / m.total_count) * 100).toFixed(1),
      }));

    return {
      topDeclineMerchants,
      topAcceptMerchants,
      totalTransactions: filteredData.length,
      totalDeclines,
      totalAccepts,
      totalDeclineAmount,
      totalAcceptAmount,
    };
  }, [filteredData]);

  // Process product-level data - grouped by product name with decline reasons
  const productSummary = useMemo(() => {
    if (!productData?.data || productData.data.length === 0) {
      return {
        products: [],
        aggregatedProducts: [],
        totalProducts: 0,
        totalDeclines: 0,
        totalDeclineAmount: 0,
        categoryBreakdown: [],
        topDeclineReasons: [],
      };
    }

    // Aggregate by product_description to combine rows with different reason codes
    const productAggregation = {};
    const reasonCodeStats = {};

    productData.data.forEach((row) => {
      const productName = row.product_description || 'Unknown';
      const declineCount = parseInt(row.decline_count) || 0;
      const declineAmount = parseFloat(row.decline_amount) || 0;
      const reasonCode = row.reason_code_description || 'Unknown';
      const responseDesc = row.response_description || 'N/A';

      // Aggregate by product
      if (!productAggregation[productName]) {
        productAggregation[productName] = {
          product_description: productName,
          category: row.spend_category || row.category_code || 'N/A',
          benefit_type: row.benefit_type || 'N/A',
          decline_count: 0,
          decline_amount: 0,
          merchant_count: parseInt(row.merchant_count) || 0,
          reasons: {},
        };
      }
      productAggregation[productName].decline_count += declineCount;
      productAggregation[productName].decline_amount += declineAmount;

      // Track reason codes per product
      const reasonKey = `${reasonCode} - ${responseDesc}`;
      if (!productAggregation[productName].reasons[reasonKey]) {
        productAggregation[productName].reasons[reasonKey] = 0;
      }
      productAggregation[productName].reasons[reasonKey] += declineCount;

      // Global reason code stats
      if (!reasonCodeStats[reasonKey]) {
        reasonCodeStats[reasonKey] = { reason: reasonKey, decline_count: 0 };
      }
      reasonCodeStats[reasonKey].decline_count += declineCount;
    });

    // Convert aggregated products to array with top reasons
    const aggregatedProducts = Object.values(productAggregation)
      .map((p) => {
        // Get top 3 reasons for this product
        const topReasons = Object.entries(p.reasons)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => `${reason} (${count})`)
          .join('; ');

        return {
          ...p,
          top_reasons: topReasons,
        };
      })
      .sort((a, b) => b.decline_count - a.decline_count)
      .slice(0, 100)
      .map((p, idx) => ({ ...p, rank: idx + 1 }));

    // Raw products for detailed view (keep original rows)
    const products = productData.data.map((row, idx) => ({
      rank: idx + 1,
      product_description: row.product_description || 'Unknown',
      category: row.spend_category || row.category_code || 'N/A',
      benefit_type: row.benefit_type || 'N/A',
      decline_count: parseInt(row.decline_count) || 0,
      decline_amount: parseFloat(row.decline_amount) || 0,
      merchant_count: parseInt(row.merchant_count) || 0,
      reason_code: row.reason_code_description || 'N/A',
      response_desc: row.response_description || 'N/A',
    }));

    // Calculate totals
    const totalDeclines = aggregatedProducts.reduce((sum, p) => sum + p.decline_count, 0);
    const totalDeclineAmount = aggregatedProducts.reduce((sum, p) => sum + p.decline_amount, 0);

    // Category breakdown
    const categoryStats = {};
    aggregatedProducts.forEach((p) => {
      const cat = p.category;
      if (!categoryStats[cat]) {
        categoryStats[cat] = { category_name: cat, decline_count: 0, decline_amount: 0 };
      }
      categoryStats[cat].decline_count += p.decline_count;
      categoryStats[cat].decline_amount += p.decline_amount;
    });
    const categoryBreakdown = Object.values(categoryStats)
      .sort((a, b) => b.decline_count - a.decline_count)
      .slice(0, 10);

    // Top 10 decline reasons globally
    const topDeclineReasons = Object.values(reasonCodeStats)
      .sort((a, b) => b.decline_count - a.decline_count)
      .slice(0, 10)
      .map((r, idx) => ({ rank: idx + 1, ...r }));

    return {
      products,
      aggregatedProducts,
      totalProducts: aggregatedProducts.length,
      totalDeclines,
      totalDeclineAmount,
      categoryBreakdown,
      topDeclineReasons,
    };
  }, [productData]);

  // Process reason code distribution data
  const reasonCodeSummary = useMemo(() => {
    if (!reasonCodeData?.data || reasonCodeData.data.length === 0) {
      return { reasonCodes: [], total: 0 };
    }

    const reasonCodes = reasonCodeData.data.map((row, idx) => ({
      rank: idx + 1,
      reason_code_description: row.reason_code_description || 'N/A',
      transaction_count: parseInt(row.transaction_count) || 0,
    })).sort((a, b) => b.transaction_count - a.transaction_count);

    const total = reasonCodes.reduce((sum, r) => sum + r.transaction_count, 0);

    return { reasonCodes, total };
  }, [reasonCodeData]);

  const declineColumns = [
    { key: 'rank', label: '#', sortable: false },
    { key: 'merchant_name', label: 'Merchant', sortable: true },
    { key: 'decline_count', label: 'Declines', sortable: true, format: 'number' },
    {
      key: 'decline_amount',
      label: 'Decline Amount',
      sortable: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: 'decline_rate',
      label: 'Decline Rate',
      sortable: true,
      render: (value) => (
        <span className="text-red-600 font-medium">{value}%</span>
      ),
    },
    { key: 'total_count', label: 'Total Txns', sortable: true, format: 'number' },
  ];

  const acceptColumns = [
    { key: 'rank', label: '#', sortable: false },
    { key: 'merchant_name', label: 'Merchant', sortable: true },
    { key: 'accept_count', label: 'Accepts', sortable: true, format: 'number' },
    {
      key: 'accept_amount',
      label: 'Accept Amount',
      sortable: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: 'accept_rate',
      label: 'Accept Rate',
      sortable: true,
      render: (value) => (
        <span className="text-green-600 font-medium">{value}%</span>
      ),
    },
    { key: 'total_count', label: 'Total Txns', sortable: true, format: 'number' },
  ];

  const productColumns = [
    { key: 'rank', label: '#', sortable: false },
    { key: 'product_description', label: 'Product', sortable: true },
    { key: 'category', label: 'Category', sortable: true },
    { key: 'decline_count', label: 'Declines', sortable: true, format: 'number' },
    {
      key: 'decline_amount',
      label: 'Amount',
      sortable: true,
      render: (value) => formatCurrency(value),
    },
    {
      key: 'merchant_count',
      label: 'Merchants',
      sortable: true,
      render: (value) => (
        <span className="text-gray-500">{value}</span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
          <p className="text-gray-500 text-lg">Analyzing transaction patterns...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-red-500">
          <AlertCircle className="w-12 h-12" />
          <p className="text-lg">Failed to load transaction data</p>
          <p className="text-sm text-gray-500">{error?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="w-6 h-6 sm:w-7 sm:h-7 text-primary-600" />
            Card Transaction Patterns
          </h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            Analysis of top merchants and products by transaction declines and accepts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 sm:flex-none">
            <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="block w-full sm:w-64 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">All Tenants</option>
              {tenants.map((tenant) => (
                <option key={tenant.tenant_code} value={tenant.tenant_code}>
                  {tenant.tenant_name}
                </option>
              ))}
            </select>
          </div>
          <QueryInfoButton
            title="Transaction Patterns Data Sources"
            queries={[CARD_METRICS_QUERIES.transactionPatterns, CARD_METRICS_QUERIES.walletTransactionDetails]}
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Transactions"
          value={formatNumber(patterns.totalTransactions)}
          icon={CreditCard}
          color="blue"
        />
        <StatCard
          title="Total Declines"
          value={formatNumber(patterns.totalDeclines)}
          subtitle={`${((patterns.totalDeclines / patterns.totalTransactions) * 100 || 0).toFixed(1)}% of total`}
          icon={TrendingDown}
          color="red"
        />
        <StatCard
          title="Total Accepts"
          value={formatNumber(patterns.totalAccepts)}
          subtitle={`${((patterns.totalAccepts / patterns.totalTransactions) * 100 || 0).toFixed(1)}% of total`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Decline Amount"
          value={formatCurrency(patterns.totalDeclineAmount)}
          icon={DollarSign}
          color="red"
        />
        <StatCard
          title="Accept Amount"
          value={formatCurrency(patterns.totalAcceptAmount)}
          icon={DollarSign}
          color="green"
        />
      </div>

      {/* Merchant Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Decline Merchants Chart */}
        <div className="card">
          <h3 className="card-header flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Top 10 Decline Merchants
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={patterns.topDeclineMerchants}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tickFormatter={formatNumber} />
                <YAxis
                  type="category"
                  dataKey="merchant_name"
                  width={150}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value?.length > 20 ? value.substring(0, 20) + '...' : value}
                />
                <Tooltip
                  formatter={(value, name) => [formatNumber(value), name === 'decline_count' ? 'Declines' : name]}
                  labelFormatter={(label) => `Merchant: ${label}`}
                />
                <Bar dataKey="decline_count" name="Declines" fill={COLORS.decline} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Accept Merchants Chart */}
        <div className="card">
          <h3 className="card-header flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Top 10 Accept Merchants
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={patterns.topAcceptMerchants}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tickFormatter={formatNumber} />
                <YAxis
                  type="category"
                  dataKey="merchant_name"
                  width={150}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value?.length > 20 ? value.substring(0, 20) + '...' : value}
                />
                <Tooltip
                  formatter={(value, name) => [formatNumber(value), name === 'accept_count' ? 'Accepts' : name]}
                  labelFormatter={(label) => `Merchant: ${label}`}
                />
                <Bar dataKey="accept_count" name="Accepts" fill={COLORS.accept} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Merchant Data Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Decline Merchants Table */}
        <div className="card">
          <h3 className="card-header flex items-center gap-2">
            <Store className="w-5 h-5 text-red-500" />
            Top 10 Decline Merchants - Details
          </h3>
          <DataTable
            data={patterns.topDeclineMerchants}
            columns={declineColumns}
            emptyMessage="No decline data available"
          />
        </div>

        {/* Top Accept Merchants Table */}
        <div className="card">
          <h3 className="card-header flex items-center gap-2">
            <Store className="w-5 h-5 text-green-500" />
            Top 10 Accept Merchants - Details
          </h3>
          <DataTable
            data={patterns.topAcceptMerchants}
            columns={acceptColumns}
            emptyMessage="No accept data available"
          />
        </div>
      </div>

      {/* Product-Level Decline Analysis Section */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-red-500" />
            Product-Level Decline Analysis
          </h3>
          {productLoading && (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading declined products...</span>
            </div>
          )}
        </div>

        {!productLoading && productSummary.totalProducts > 0 && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Declined Products"
                value={formatNumber(productSummary.totalProducts)}
                subtitle="Unique products with declines"
                icon={Package}
                color="red"
              />
              <StatCard
                title="Total Declines"
                value={formatNumber(productSummary.totalDeclines)}
                subtitle="From record_type = 'R'"
                icon={TrendingDown}
                color="red"
              />
              <StatCard
                title="Decline Amount"
                value={formatCurrency(productSummary.totalDeclineAmount)}
                icon={DollarSign}
                color="red"
              />
              <StatCard
                title="Categories"
                value={formatNumber(productSummary.categoryBreakdown.length)}
                icon={Store}
                color="blue"
              />
            </div>

            {/* Top 10 Decline Reasons Summary */}
            {productSummary.topDeclineReasons.length > 0 && (
              <div className="card mb-6">
                <h3 className="card-header flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  Top 10 Decline Reasons (All Products)
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={productSummary.topDeclineReasons}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tickFormatter={formatNumber} />
                        <YAxis
                          type="category"
                          dataKey="reason"
                          width={200}
                          tick={{ fontSize: 9 }}
                          tickFormatter={(value) => value?.length > 35 ? value.substring(0, 35) + '...' : value}
                        />
                        <Tooltip
                          formatter={(value) => [formatNumber(value), 'Declines']}
                        />
                        <Bar dataKey="decline_count" name="Declines" fill={COLORS.decline} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <DataTable
                    data={productSummary.topDeclineReasons}
                    columns={[
                      { key: 'rank', label: '#', sortable: false },
                      { key: 'reason', label: 'Decline Reason', sortable: true },
                      { key: 'decline_count', label: 'Count', sortable: true, format: 'number' },
                    ]}
                    emptyMessage="No decline reasons"
                    pageSize={10}
                  />
                </div>
              </div>
            )}

            {/* Charts and Tables Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Category Breakdown Chart */}
              <div className="card">
                <h3 className="card-header flex items-center gap-2">
                  <Store className="w-5 h-5 text-red-500" />
                  Declines by Category
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productSummary.categoryBreakdown}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={formatNumber} />
                      <YAxis
                        type="category"
                        dataKey="category_name"
                        width={120}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => value?.length > 15 ? value.substring(0, 15) + '...' : value}
                      />
                      <Tooltip
                        formatter={(value) => [formatNumber(value), 'Declines']}
                      />
                      <Bar dataKey="decline_count" name="Declines" fill={COLORS.decline} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Category Amount Chart */}
              <div className="card">
                <h3 className="card-header flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-500" />
                  Decline Amount by Category
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productSummary.categoryBreakdown}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                      <YAxis
                        type="category"
                        dataKey="category_name"
                        width={120}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => value?.length > 15 ? value.substring(0, 15) + '...' : value}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Amount']}
                      />
                      <Bar dataKey="decline_amount" name="Amount" fill="#F97316" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Products Table - Aggregated with Top Reasons */}
            <div className="card">
              <h3 className="card-header flex items-center gap-2">
                <Package className="w-5 h-5 text-red-500" />
                Top 100 Declined Products (with Top Decline Reasons)
              </h3>
              <DataTable
                data={productSummary.aggregatedProducts}
                columns={[
                  { key: 'rank', label: '#', sortable: false },
                  { key: 'product_description', label: 'Product', sortable: true },
                  { key: 'category', label: 'Category', sortable: true },
                  { key: 'decline_count', label: 'Declines', sortable: true, format: 'number' },
                  { key: 'decline_amount', label: 'Amount', sortable: true, render: (v) => formatCurrency(v) },
                  {
                    key: 'top_reasons',
                    label: 'Top Decline Reasons',
                    sortable: false,
                    render: (v) => (
                      <span className="text-xs text-gray-600">{v || 'N/A'}</span>
                    ),
                  },
                ]}
                emptyMessage="No declined product data"
                pageSize={20}
              />
            </div>
          </>
        )}

        {!productLoading && productSummary.totalProducts === 0 && (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No product-level decline data available</p>
            <p className="text-sm mt-1">Check if daily_item_level_v2 table has data with record_type = 'R'</p>
          </div>
        )}
      </div>

      {/* Reason Code Distribution Section */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-purple-500" />
            Reason Code Distribution
          </h3>
          {reasonCodeLoading && (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading reason codes...</span>
            </div>
          )}
        </div>

        {!reasonCodeLoading && reasonCodeSummary.reasonCodes.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Reason Code Chart */}
            <div className="card">
              <h3 className="card-header flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Transactions by Reason Code
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={reasonCodeSummary.reasonCodes.slice(0, 15)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tickFormatter={formatNumber} />
                    <YAxis
                      type="category"
                      dataKey="reason_code_description"
                      width={180}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => value?.length > 25 ? value.substring(0, 25) + '...' : value}
                    />
                    <Tooltip
                      formatter={(value) => [formatNumber(value), 'Transactions']}
                    />
                    <Bar
                      dataKey="transaction_count"
                      name="Transactions"
                      fill="#8B5CF6"
                      radius={[0, 4, 4, 0]}
                      cursor="pointer"
                      onClick={(data, index, event) => {
                        const reasonCode = data?.payload?.reason_code_description || data?.reason_code_description;
                        if (reasonCode) {
                          setSelectedReasonCode(reasonCode);
                        }
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-gray-500 mt-2 px-4 pb-2">Click on a bar to see merchants for that reason code</p>
            </div>

            {/* Reason Code Table */}
            <div className="card">
              <h3 className="card-header flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                All Reason Codes ({reasonCodeSummary.reasonCodes.length})
                <span className="text-xs text-gray-400 ml-2">Click a row to drill down</span>
              </h3>
              <DataTable
                data={reasonCodeSummary.reasonCodes}
                columns={[
                  { key: 'rank', label: '#', sortable: false },
                  {
                    key: 'reason_code_description',
                    label: 'Reason Code Description',
                    sortable: true,
                    render: (value, row) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReasonCode(row.reason_code_description);
                        }}
                        className="text-left text-purple-600 hover:text-purple-800 hover:underline flex items-center gap-1 font-medium"
                      >
                        {value || row.reason_code_description}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    ),
                  },
                  { key: 'transaction_count', label: 'Transactions', sortable: true, format: 'number' },
                ]}
                emptyMessage="No reason code data"
              />
            </div>
          </div>
        )}

        {!reasonCodeLoading && reasonCodeSummary.reasonCodes.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No reason code data available</p>
          </div>
        )}

        {/* Reason Code Drill-Down Panel */}
        {selectedReasonCode && (
          <div className="mt-6 card border-2 border-purple-200 bg-purple-50">
            <div className="flex items-center justify-between p-4 border-b border-purple-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Store className="w-5 h-5 text-purple-500" />
                Merchants for: <span className="text-purple-600 bg-purple-100 px-2 py-1 rounded">{selectedReasonCode}</span>
              </h3>
              <button
                onClick={() => setSelectedReasonCode(null)}
                className="p-2 hover:bg-purple-100 rounded-full transition-colors"
                title="Close drill-down"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-4 bg-white">
              {merchantsLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                  <span className="ml-2 text-gray-500">Loading merchants...</span>
                </div>
              )}

              {!merchantsLoading && merchantsData?.data && merchantsData.data.length > 0 && (
                <DataTable
                  data={merchantsData.data.map((row, idx) => ({
                    rank: idx + 1,
                    merchant_name: row.merchant_name || 'Unknown',
                    product_description: row.product_description || 'N/A',
                    spend_category: row.spend_category || 'N/A',
                    benefit_type: row.benefit_type || 'N/A',
                    response_description: row.response_description || 'N/A',
                    decline_count: parseInt(row.decline_count) || 0,
                    decline_amount: parseFloat(row.decline_amount) || 0,
                  }))}
                  columns={[
                    { key: 'rank', label: '#', sortable: false },
                    { key: 'merchant_name', label: 'Merchant', sortable: true },
                    { key: 'product_description', label: 'Product', sortable: true },
                    { key: 'spend_category', label: 'Category', sortable: true },
                    { key: 'decline_count', label: 'Declines', sortable: true, format: 'number' },
                    { key: 'decline_amount', label: 'Amount', sortable: true, render: (v) => formatCurrency(v) },
                    { key: 'response_description', label: 'Response', sortable: true },
                  ]}
                  emptyMessage="No merchant data for this reason code"
                />
              )}

              {!merchantsLoading && (!merchantsData?.data || merchantsData.data.length === 0) && (
                <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                  <Store className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No merchants found for this reason code</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
